import { Component, ViewChild, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { MatOptionSelectionChange } from '@angular/material/core';
import { AgGridAngular } from 'ag-grid-angular';
import { CommonDimensions } from '../models/collection.interface';
import { ColDef, RowSelectionOptions, themeMaterial, RowSelectedEvent } from 'ag-grid-community';
import { MatSidenav } from '@angular/material/sidenav';
import { CollectionsService } from '../services/collections.service';
import { Collection } from '../models/collection.interface';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

type FilterKey = 'States' | 'Years';

function formatIngestedBucket(isoOrDate: string | Date): string {
  const d = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(d.getTime())) return '—';
  const now = Date.now();
  const diff = now - d.getTime();

  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  const d7 = 7 * day;
  const d30 = 30 * day;

  if (diff < hr) return 'Last hour';
  if (diff < day) return 'Last 24 hours';
  if (diff < d7) return 'Last 7 days';
  if (diff < d30) return 'Last 30 days';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

interface DocRow {
  state: string;
  utility: string;
  filingType: string;
  proceeding: { number: string };

  ingestedAt: string; // ISO string
  title: string;
  entity?: string;
  type?: string;
  industry?: string;
  company?: string;
  year?: number;
  summary?: string;
  filingDate?: string;
}

type ColumnKey = 'state' | 'company' | 'proceeding' | 'type' | 'year';
type MatchOp = 'equals' | 'contains' | 'starts';

interface RuleCondition {
  column: ColumnKey;
  matchOperator: MatchOp;
  value: string;
}

@Component({
  selector: 'app-libraries',
  templateUrl: './libraries.component.html',
  styleUrls: ['./libraries.component.scss']
})
export class LibrariesComponent implements OnInit {
  showFilters = false;
  @ViewChild('agGrid') agGrid!: AgGridAngular;
  @ViewChild('rulesSidenav') rulesSidenav!: MatSidenav;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;

  openRules() {
    this.rulesSidenav?.open();
  }

  closeRules() {
    this.rulesSidenav?.close();
  }

  toggleRules() {
    this.rulesSidenav?.toggle();
  }

  selectedRows: CommonDimensions[] = [];

  theme = themeMaterial;

  // Track if user changed dropdowns but hasn't clicked Apply yet
  pendingFilterChanges = false;

  // Track if any AG Grid column menu filters are active
  columnFiltersDirty = false;

  isTargetAutocompleteOpen = false;
  isColumnAutocompleteOpen = false;

  columnOptions: { value: ColumnKey; label: string }[] = [
    { value: 'state', label: 'State' },
    { value: 'company', label: 'Company' },
    { value: 'proceeding', label: 'Proceeding Number' },
    { value: 'type', label: 'Filing Type' },
    { value: 'year', label: 'Year' }
  ];

  conditions: RuleCondition[] = [this.createDefaultCondition()];

  // Count of active “top bar” filters
  get activeFilterCount(): number {
    return this.filterTypes.reduce((acc, key) => {
      const sel = this.selectedFilters[key] ?? [];
      // active if not just ALL_TOKEN
      return acc + (sel.length === 1 && sel[0] === this.ALL_TOKEN ? 0 : sel.length ? 1 : 0);
    }, 0);
  }

  rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'multiRow',
    selectAll: 'filtered',
    enableClickSelection: true
  };

  defaultColDef: ColDef = {
    flex: 1,
    minWidth: 140,
    sortable: true,
    resizable: true,
    filter: 'agTextColumnFilter', // text filter by default
    filterParams: {
      debounceMs: 200,
      buttons: ['reset'] // optional tiny reset button in filter
    }
  };

  colDefs: ColDef[] = [
    {
      field: 'ingestedAt',
      headerName: 'Ingested',
      flex: 1,
      valueFormatter: p => formatIngestedBucket(p.value),
      tooltipValueGetter: p => new Date(p.data?.ingestedAt).toLocaleString(),
      comparator: (a, b) => new Date(a).getTime() - new Date(b).getTime(),
      sort: 'desc',
      filter: 'agDateColumnFilter',
      filterParams: {
        // compare a date picker value to your ISO string in the row
        comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          // normalize to midnight for comparison
          const pick = new Date(
            filterLocalDateAtMidnight.getFullYear(),
            filterLocalDateAtMidnight.getMonth(),
            filterLocalDateAtMidnight.getDate()
          ).getTime();
          const cell = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate()).getTime();
          return cell === pick ? 0 : cell < pick ? -1 : 1;
        },
        debounceMs: 200
      }
    },
    { field: 'state', headerName: 'State', flex: 0.8 },
    { field: 'company', headerName: 'Company', flex: 1 },
    { field: 'proceeding.number', headerName: 'Proceeding #', flex: 1, filter: 'agTextColumnFilter' },
    {
      field: 'year',
      headerName: 'Year',
      width: 110,
      sortable: true,
      filter: 'agNumberColumnFilter', // numeric filter
      // ensure filtering compares numbers, not strings:
      valueGetter: p => p.data?.year ?? null
    },
    { field: 'title', headerName: 'Title', flex: 2, tooltipField: 'title' }, // uses text filter
    {
      headerName: 'Summary',
      field: 'summary',
      flex: 2,
      filter: 'agTextColumnFilter',
      tooltipField: 'summary'
    }
  ];

  // Order of filters in the bar
  filterTypes: FilterKey[] = ['States', 'Years'];

  selectedFilters: Record<string, string[]> = {
    States: ['All States'],
    Years: ['All Years']
  };

  filterOptions: Record<string, string[]> = {
    States: ['All States', 'CA', 'TX'],
    Years: ['All Years', '2023', '2024']
  };

  // replace with API data later
  baseOptions: Record<FilterKey, string[]> = {
    States: ['CA', 'TX', 'WA', 'NY', 'CO', 'AZ', 'NV', 'OR', 'ID'],
    Years: ['2025', '2024', '2023', '2022']
  };

  readonly ALL_TOKEN = '__ALL__';

  pastAndFuture: 'pastAndFuture' | 'future' = 'pastAndFuture';
  matchOperator: 'equals' | 'contains' | 'starts' = 'equals';

  collections: Collection[] = [];
  collectionInput: Collection | null = {} as Collection; // Input for autocomplete
  filteredCollections: Collection[] = []; // Filtered collections for autocomplete
  selectedCollection: Collection | undefined = undefined;

  isEmpty(ft: FilterKey): boolean {
    const sel = this.selectedFilters[ft] ?? [];
    return sel.filter(v => v !== this.ALL_TOKEN).length === 0;
  }

  constructor(private collectionsService: CollectionsService) {}

  ngOnInit(): void {
    this.getCollections();
    this.filterTypes.forEach(key => {
      const allReal = this.baseOptions[key] ?? [];
      this.selectedFilters[key] = [this.ALL_TOKEN, ...allReal];
      this.applyFilters();
    });
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  allLabel(key: FilterKey): string {
    return `All ${this.labelFor(key)}`;
  }

  labelFor(key: FilterKey): string {
    return key;
  }

  toggleAll(ft: FilterKey, ev: MatOptionSelectionChange) {
    if (!ev.isUserInput) return;
    const allReal = this.baseOptions[ft] ?? [];

    Promise.resolve().then(() => {
      if (ev.source.selected) {
        // Show "All ..." as selected AND check every real option
        this.selectedFilters[ft] = [this.ALL_TOKEN, ...allReal];
      } else {
        // Unselecting "All ..." -> clear all (or leave empty to force a choice)
        this.selectedFilters[ft] = [];
      }

      // mark pending
      this.pendingFilterChanges = true;
    });
  }

  toggleSpecific(ft: FilterKey, option: string, ev: MatOptionSelectionChange) {
    if (!ev.isUserInput) return;
    const allReal = this.baseOptions[ft] ?? [];
    let sel = [...(this.selectedFilters[ft] ?? [])];

    if (ev.source.selected) {
      sel = sel.filter(v => v !== this.ALL_TOKEN);
      if (!sel.includes(option)) sel.push(option);
    } else {
      sel = sel.filter(v => v !== option);
    }

    const withoutAll = sel.filter(v => v !== this.ALL_TOKEN);

    const finalSel =
      withoutAll.length === allReal.length && allReal.length > 0 ? [this.ALL_TOKEN, ...withoutAll] : withoutAll;

    Promise.resolve().then(() => {
      this.selectedFilters[ft] = finalSel;
      this.pendingFilterChanges = true;
    });
  }

  onFilterChange(filterType: FilterKey, event: MatSelectChange) {
    const allReal = this.baseOptions[filterType] ?? [];

    const prev = this.selectedFilters[filterType] ?? [];
    const next = [...(event.value ?? [])];

    const hadAll = prev.includes(this.ALL_TOKEN);
    const hasAll = next.includes(this.ALL_TOKEN);

    if (!hadAll && hasAll) {
      this.selectedFilters[filterType] = [this.ALL_TOKEN, ...allReal];
      return;
    }

    if (hadAll && !hasAll) {
      this.selectedFilters[filterType] = [];
      return;
    }

    if (hasAll) {
      const realCount = next.filter(v => v !== this.ALL_TOKEN).length;
      if (realCount < allReal.length) {
        this.selectedFilters[filterType] = next.filter(v => v !== this.ALL_TOKEN);
        return;
      }
    }

    const cleaned = next.filter(v => v !== this.ALL_TOKEN);
    if (cleaned.length === allReal.length && allReal.length > 0) {
      this.selectedFilters[filterType] = [this.ALL_TOKEN, ...cleaned];
    } else {
      this.selectedFilters[filterType] = cleaned;
    }
  }

  // Helper for the trigger text
  displayTriggerText(ft: FilterKey): string {
    const sel = this.selectedFilters[ft] ?? [];
    if (sel.includes(this.ALL_TOKEN)) return this.allLabel(ft);
    const real = sel.filter(v => v !== this.ALL_TOKEN);
    return real.length ? real.join(', ') : '— Select at least one —';
  }

  // Map FilterKey to the field on DocRow
  private filterField(key: FilterKey): keyof DocRow {
    switch (key) {
      case 'States':
        return 'state';
      case 'Years':
        return 'year';
    }
  }

  // Normalize a row value to compare against selections
  private normalizeForCompare(
    key: FilterKey,
    rowValue: string | number | { number: string; description?: string } | undefined
  ): string {
    if (key === 'Years') return String(rowValue ?? '');
    if (typeof rowValue === 'object' && rowValue !== null && 'number' in rowValue) {
      return String(rowValue.number ?? '').trim();
    }
    return String(rowValue ?? '').trim();
  }

  // Build a Set of selected values for a given filter
  private selectedSet(key: FilterKey): Set<string> {
    const sel = this.selectedFilters[key] ?? [];
    // store ALL_TOKEN + all real options when "All ..." is active.
    const real = sel.filter(v => v !== this.ALL_TOKEN);
    return new Set(real);
  }

  onSearchChange(v: string) {
    this.gridFilterText = v ?? '';
    this.applyFilters(); // reuse existing pipeline
  }

  private textContains(haystack: unknown, needle: string): boolean {
    if (!needle) return true; // no keyword constraint
    const h = (haystack ?? '').toString().toLowerCase();
    return h.includes(needle.toLowerCase());
  }

  private rowMatchesKeyword(row: DocRow, q: string): boolean {
    if (!q) return true;
    // searchable fields:
    return (
      this.textContains(row.title, q) ||
      this.textContains(row.company, q) ||
      this.textContains(row.state, q) ||
      this.textContains(row.year, q) ||
      this.textContains(row.summary, q) ||
      this.textContains(row.filingType, q) ||
      this.textContains(row.filingDate, q) ||
      this.textContains(row.proceeding?.number, q)
    );
  }

  applyFilters(): void {
    const sets = {
      States: this.selectedSet('States'),
      Years: this.selectedSet('Years')
    } as const;

    const q = (this.gridFilterText || '').trim();

    this.filteredRowData = this.allRowData.filter(row => {
      for (const key of this.filterTypes) {
        const set = sets[key];
        if (!set) continue;

        if (set.size === 0) continue;

        const field = this.filterField(key);
        const rowVal = this.normalizeForCompare(key, row[field]);
        if (!set.has(rowVal)) return false;
      }
      return this.rowMatchesKeyword(row, q);
    });
  }

  onRowSelected(event: RowSelectedEvent): void {
    this.selectedRows = this.agGrid.api.getSelectedRows();
    const currentRow = event.node.data;
    console.log('Selected row:', currentRow);
  }

  get showSelectedDocumentsMenu() {
    return this.selectedRows.length > 0;
  }

  clearAllFilters(): void {
    this.filterTypes.forEach(key => {
      this.selectedFilters[key] = [];
    });

    // Search box
    this.gridFilterText = '';

    if (this.agGrid?.api) {
      this.agGrid.api.setFilterModel(null);
      this.agGrid.api.setGridOption('quickFilterText', '');
    }

    this.applyFiltersFromUI?.();

    this.pendingFilterChanges = false;
    this.columnFiltersDirty = false;
  }

  areTopFiltersValid(): boolean {
    return this.filterTypes.every(ft => {
      const sel = this.selectedFilters[ft] ?? [];
      // require at least one REAL selection OR the ALL token
      return sel.length > 0;
    });
  }

  applyFiltersFromUI(): void {
    if (!this.areTopFiltersValid()) {
      return;
    }
    this.applyFilters();
    this.pendingFilterChanges = false;

    if (this.agGrid?.api) {
      const model = this.agGrid.api.getFilterModel() ?? {};
      this.columnFiltersDirty = Object.keys(model).length > 0;
    }
  }

  // Collections Search box
  clearInputText() {
    this.collectionInput = null;
    this.filteredCollections = [...this.collections];
  }

  displayFn(collection: Collection | null): string {
    // When a collection is selected, display its name
    return collection ? collection.name : '';
  }

  filterCollections(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();

    // Filter collections based on the input value
    this.filteredCollections = this.collections.filter(collection =>
      collection.name.toLowerCase().includes(filterValue)
    );

    // If no collections match and we have a selected collection, add it to the filtered list
    if (this.filteredCollections.length === 0 && this.selectedCollection) {
      if (this.selectedCollection.name.toLowerCase().includes(filterValue)) {
        this.filteredCollections = [this.selectedCollection];
      }
    }
  }

  selectTopCollection(event: Event): void {
    event.preventDefault();

    // If there are filtered collections, select the first one
    if (this.filteredCollections.length > 0) {
      // const topCollection = this.filteredCollections[0];
      // this.setCollection(topCollection);
      console.log('Top collection selected:', this.filteredCollections[0]);

      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.closePanel();
      }
    }
  }

  getCollections() {
    const selectedCollectionId = this.selectedCollection?.collection_id;
    this.collectionsService.getCollections(true).subscribe({
      next: data => {
        this.collections = data;
        // Sort collections by name in alphabetical order
        this.filteredCollections = [...this.collections]; // Initialize filtered collections
        let lastActiveCollection = this.collections.find(c => c.collection_id === selectedCollectionId);
        if (!lastActiveCollection) {
          // No session found, set default to top collection
          lastActiveCollection = this.collections[0];
        }
        // this.setCollection(lastActiveCollection);
      },
      error: error => {
        console.error('There was an error while fetching the collections: ', error);
      }
    });
  }

  addCondition(): void {
    this.conditions.push(this.createDefaultCondition());
  }

  removeCondition(index: number): void {
    if (index > 0) {
      this.conditions.splice(index, 1);
    }
  }

  trackByIndex(i: number): number {
    return i;
  }

  private createDefaultCondition(): RuleCondition {
    return {
      column: 'state',
      matchOperator: 'equals',
      value: ''
    };
  }

  // --- Mock Data ---
  allRowData: DocRow[] = [
    {
      state: 'CA',
      utility: 'SDG&E',
      filingType: 'Wildfire Mitigation Plan',
      proceeding: { number: 'A2505009' },
      ingestedAt: new Date(Date.now() - 45 * 60_000).toISOString(), // 45 minutes ago -> Last hour
      title: 'California–Energy–Report.pdf',
      entity: 'Green Future Energy',
      type: 'Energy Report',
      industry: 'Energy',
      company: 'Green Future LLC',
      summary: 'Mitigation plan proceeding for 2024 covering wildfire safety strategies',
      year: 2025
    },
    {
      state: 'CA',
      utility: 'PG&E',
      filingType: 'General Rate Case',
      proceeding: { number: 'A2403001' },
      ingestedAt: new Date(Date.now() - 3 * 24 * 3600_000).toISOString(), // 3 days ago -> Last 7 days
      title: 'PG&E GRC Application.pdf',
      entity: 'PG&E',
      type: 'GRC',
      industry: 'Energy',
      company: 'PG&E',
      summary: 'GRC filing for 2024 including rate adjustments and infrastructure investments',
      year: 2024
    },
    {
      state: 'TX',
      utility: 'TXU',
      filingType: 'General Rate Case',
      proceeding: { number: 'A2301002' },
      ingestedAt: '2024-02-15T12:00:00Z', // older -> exact date
      title: 'TXU Rate Case – Filing',
      entity: 'TXU',
      type: 'GRC',
      industry: 'Energy',
      company: 'TXU',
      summary: 'Rate case filing for 2023 focusing on grid reliability improvements',
      year: 2023
    },
    {
      state: 'CA',
      utility: 'PG&E',
      filingType: 'General Rate Case',
      proceeding: { number: 'A2403001' }, // same as above
      ingestedAt: new Date(Date.now() - 5 * 24 * 3600_000).toISOString(),
      title: 'PG&E Rate Case Supplemental.pdf',
      company: 'PG&E',
      year: 2024,
      summary: 'Supplemental filing for the ongoing 2024–2026 case',
      filingDate: '2024-04-02'
    },
    {
      state: 'CA',
      utility: 'PG&E',
      filingType: 'General Rate Case',
      proceeding: { number: 'A2403001' }, // same proceeding again
      ingestedAt: new Date(Date.now() - 10 * 24 * 3600_000).toISOString(),
      title: 'PG&E Testimony.pdf',
      company: 'PG&E',
      year: 2024,
      summary: 'Expert testimony document submitted for rate case',
      filingDate: '2024-04-08'
    },
    {
      state: 'WA',
      utility: 'Seattle City Light',
      filingType: 'Technology Filing',
      proceeding: { number: 'A2507001' },
      ingestedAt: new Date(Date.now() - 2 * 60 * 60_000).toISOString(), // 2 hours ago
      title: 'Smart Grid Tech Report.pdf',
      company: 'Seattle City Light',
      year: 2025,
      summary: 'Technology adoption report for grid modernization',
      filingDate: '2025-07-15'
    },
    {
      state: 'NY',
      utility: 'Con Edison',
      filingType: 'Wildfire Mitigation Plan',
      proceeding: { number: 'A2508002' },
      ingestedAt: new Date(Date.now() - 7 * 24 * 3600_000).toISOString(), // 7 days ago
      title: 'ConEd Mitigation Plan.pdf',
      company: 'Con Edison',
      year: 2025,
      summary: 'Mitigation plan submission for New York utilities',
      filingDate: '2025-08-01'
    }
  ];

  filteredRowData: DocRow[] = this.allRowData.slice();

  // Search input binding
  gridFilterText = '';

  onQuickFilterChanged(value: string) {
    this.gridFilterText = value;
    if (this.agGrid?.api) {
      this.agGrid.api.updateGridOptions({ quickFilterText: this.gridFilterText });
    }
  }
}
